import { useEffect, useRef, useState } from 'react';
import {
  GEOJSON_MAX_FILE_BYTES,
  type GeoJsonImportError,
  parseProductionPlaceGeoJson,
} from '@/features/production-declaration/components/statement/production-place/production-place-geojson';
import type { ProductionPlaceCoordinates } from '@/types/core/production-declaration/production-place';

type ImportState =
  | { status: 'idle' | 'success' }
  | { status: 'reading'; previousValue: ProductionPlaceCoordinates | null }
  | { status: 'error'; error: GeoJsonImportError | 'readError' }
  | {
      status: 'confirm';
      fileName: string;
      geometry: ProductionPlaceCoordinates;
      previousValue: ProductionPlaceCoordinates | null;
    };
type ImportParams = {
  disabled: boolean;
  value: ProductionPlaceCoordinates | null;
  onChange: (geometry: ProductionPlaceCoordinates) => void;
};

export function useProductionPlaceGeoJsonImport({
  disabled,
  value,
  onChange,
}: ImportParams) {
  const [state, setState] = useState<ImportState>({ status: 'idle' });
  const [fileName, setFileName] = useState<string | null>(null);
  const requestRef = useRef(0);
  const currentRef = useRef({ disabled, value, onChange });

  currentRef.current = { disabled, value, onChange };

  useEffect(() => {
    setState((current) =>
      current.status === 'reading' || current.status === 'confirm'
        ? disabled || current.previousValue !== value
          ? { status: 'idle' }
          : current
        : current,
    );

    return () => {
      requestRef.current++;
    };
  }, [disabled, value]);

  const applyGeometry = (
    geometry: ProductionPlaceCoordinates,
    importedFileName: string,
  ) => {
    currentRef.current.onChange(geometry);
    setFileName(importedFileName);
    setState({ status: 'success' });
  };

  const importFile = async (file: File) => {
    if (currentRef.current.disabled) {
      return;
    }

    const request = ++requestRef.current;
    const previousValue = currentRef.current.value;
    const isCurrent = () =>
      request === requestRef.current &&
      !currentRef.current.disabled &&
      currentRef.current.value === previousValue;

    if (file.size > GEOJSON_MAX_FILE_BYTES) {
      setState({ status: 'error', error: 'fileTooLarge' });
      return;
    }

    setState({ status: 'reading', previousValue });

    try {
      const text = await file.text();

      if (!isCurrent()) {
        return;
      }

      const result = parseProductionPlaceGeoJson(text);

      if (result.status === 'error') {
        setState(result);
      } else if (
        previousValue &&
        previousValue.coordinates.length > 0 &&
        (previousValue.type === 'Polygon' ||
          previousValue.coordinates.some((polygon) => polygon.length > 0))
      ) {
        setState({
          status: 'confirm',
          fileName: file.name,
          geometry: result.geometry,
          previousValue,
        });
      } else {
        applyGeometry(result.geometry, file.name);
      }
    } catch {
      if (isCurrent()) {
        setState({ status: 'error', error: 'readError' });
      }
    }
  };

  const confirmImport = () => {
    if (
      state.status === 'confirm' &&
      !currentRef.current.disabled &&
      currentRef.current.value === state.previousValue
    ) {
      applyGeometry(state.geometry, state.fileName);
    }
  };

  return {
    cancelImport: () => setState({ status: 'idle' }),
    confirmImport,
    fileName,
    importFile,
    state,
  };
}
